import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ChannelContext, ConfigContext, UserContext } from '../../context';
import {
  ChannelAppTabAction,
  useStoriesByChannelIdQuery,
  useStoryCardCategoriesByChannelIdQuery,
  useStoryCardTemplatesByChannelIdQuery,
} from '../../services';
import {
  ChannelSwitcher,
  ConfigurationLink,
  CreateCard,
  getAppTabHrefByActionType,
  getWebAppUrl,
  optArrItem,
  useAppSelector,
  useIsNarrowViewport,
} from '../../shared';
import { Footer } from '../Footer';
import { Menu } from '../Menu';
import { SpecialTeaser } from '../SpecialTeaser';

import classNames from 'classnames';
import classes from './Sidebar.module.scss';

interface SidebarProps {
  onMobilePanelClose?: () => void;
}

export const Sidebar: FunctionComponent<SidebarProps> = memo(({ onMobilePanelClose }) => {
  const { t } = useTranslation();

  const isNarrowViewport = useIsNarrowViewport();

  const { id: channelId, appTabs: tabs } = useContext(ChannelContext).channel;

  const { id: userId } = useContext(UserContext).userInfo.userData;

  const { userDirectory, bookmarks, headerLinks } = useContext(ConfigContext).config.elements;

  const {
    showAsPrimaryTab: userDirectoryShowAsPrimaryTab,
    primaryTabIconUrl: userDirectoryPrimaryTabIconUrl,
    localiseKey: userDirectoryLocaliseKey,
  } = userDirectory;

  const { showAsPrimaryTab: bookmarksShowAsPrimaryTab } = bookmarks;

  const appTabs = useMemo(
    () => [
      ...tabs,
      ...optArrItem(userDirectoryShowAsPrimaryTab, {
        iconUrl: userDirectoryPrimaryTabIconUrl,
        action: { type: ChannelAppTabAction.USER_DIRECTORY },
        title: [
          { language: 'en-US', localization: t(userDirectoryLocaliseKey) },
          { language: 'de-DE', localization: t(userDirectoryLocaliseKey) },
        ],
      }),
      ...optArrItem(bookmarksShowAsPrimaryTab, {
        iconUrl: 'https://mxz-assets.tchop.it/img/app-icons/default/bookmarks.svg',
        action: { type: ChannelAppTabAction.BOOKMARKS },
        title: [
          { language: 'en-US', localization: t('secondaryTabs.bookmarks') },
          { language: 'de-DE', localization: t('secondaryTabs.bookmarks') },
        ],
      }),
    ],
    [
      bookmarksShowAsPrimaryTab,
      t,
      tabs,
      userDirectoryLocaliseKey,
      userDirectoryPrimaryTabIconUrl,
      userDirectoryShowAsPrimaryTab,
    ]
  );

  const { channels } = useAppSelector(({ channels }) => channels);

  const { data: postInStories = [], isLoading: isLoadingPostInStories } =
    useStoriesByChannelIdQuery({ channelId, filter: { onlyPostIn: true } });

  const { data: categories = [], isLoading: isLoadingCategories } =
    useStoryCardCategoriesByChannelIdQuery({ channelId });

  const { data: templates = [], isLoading: isLoadingTemplates } =
    useStoryCardTemplatesByChannelIdQuery({ channelId });

  const hasAppTabs = useMemo(() => Boolean(appTabs.length), [appTabs.length]);

  const onChannelChange = useCallback(
    (channelId: number) => {
      if (!hasAppTabs) {
        return;
      }

      const selectedChannel = channels
        .find(({ id }) => id === channelId)
        ?.subdomain.replace(/\s/g, '');

      if (!selectedChannel) {
        return;
      }

      const appTabHref = getAppTabHrefByActionType({
        actionType: appTabs[0].action.type,
        storyId: appTabs[0].action.storyId,
      });

      window.location.replace(
        `${getWebAppUrl(selectedChannel)}${appTabHref !== '/' ? appTabHref : ''}`
      );
    },
    [appTabs, channels, hasAppTabs]
  );

  const renderChannelSwitcher = useMemo(
    () => Boolean(hasAppTabs && channels.length),
    [channels.length, hasAppTabs]
  );

  const hasPostInStories = useMemo(() => Boolean(postInStories.length), [postInStories.length]);

  const hasCategories = useMemo(() => Boolean(categories.length), [categories.length]);

  const postInTemplates = useMemo(
    () => templates.filter(({ story }) => postInStories.some(({ id }) => id === story?.id)),
    [postInStories, templates]
  );

  const renderCardCreate = useMemo(() => {
    if (isLoadingPostInStories || isLoadingCategories || isLoadingTemplates) {
      return <Skeleton height={'2.5rem'} />;
    }

    if (!hasPostInStories && !hasCategories) {
      return null;
    }

    return (
      <CreateCard
        postInStories={postInStories}
        categories={categories}
        templates={postInTemplates}
        onMobilePanelClose={onMobilePanelClose}
      />
    );
  }, [
    categories,
    hasCategories,
    hasPostInStories,
    isLoadingCategories,
    isLoadingPostInStories,
    isLoadingTemplates,
    onMobilePanelClose,
    postInStories,
    postInTemplates,
  ]);

  const appTabsRelatedContent = useMemo(() => {
    return (
      <>
        {renderChannelSwitcher && <ChannelSwitcher onChange={onChannelChange} />}
        {hasAppTabs && (
          <nav>
            <Menu
              appTabs={appTabs}
              hasPostInStories={hasPostInStories}
              channelId={channelId}
              userId={userId}
            />
          </nav>
        )}
      </>
    );
  }, [
    appTabs,
    channelId,
    hasAppTabs,
    hasPostInStories,
    onChannelChange,
    renderChannelSwitcher,
    userId,
  ]);

  return (
    <div
      className={classNames(classes['sidebar'], {
        [classes['sidebar--narrow']]: isNarrowViewport,
      })}
    >
      {appTabsRelatedContent}

      {renderCardCreate}

      {isNarrowViewport && Boolean(headerLinks.length) && (
        <div className={classes['sidebar__header-links']}>
          {headerLinks.map((link) => (
            <ConfigurationLink key={link.title} link={link} clickCallback={onMobilePanelClose} />
          ))}
        </div>
      )}

      {channelId === 128 && (
        <iframe
          title={'ZeiterfassungPortal'}
          width={'calc(100% - 1rem)'}
          height={'300'}
          src={'https://pos.prod.d003.loc/ESSApps/Time/ZeiterfassungPortal'}
        />
      )}

      <SpecialTeaser />

      <Footer />
    </div>
  );
});
